import { useState } from "react";
import { Lock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { usePreviewCredentials, useSaveCredentials } from "../../hooks/useCredentials";
import { errorMessage } from "../../hooks/useServers";

const STATUS_LABEL = {
  new: { icon: "🟢", text: "Nuevo" },
  update: { icon: "🟡", text: "Se actualizará" },
  same: { icon: "✅", text: "Ya estaba guardado" },
  problem: { icon: "❌", text: "No se puede guardar" },
};
const ROW_ICON = { success: "✅", error: "❌", warning: "🟡", pending: "🟡" };
const typeLabel = (t) => (t === "routeros" ? "MikroTik RouterOS (API)" : t === "olt" ? "OLT Huawei (Telnet)" : "Servidor Linux (SSH)");

function PasteBox({ projectId, onClose }) {
  const [text, setText] = useState("");
  const [allowChanges, setAllowChanges] = useState(true); // por defecto la IA puede proponer cambios en servidores Linux (siempre con tu botón de confirmar)
  const [step, setStep] = useState("paste"); // paste | review | done
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const previewMutation = usePreviewCredentials(projectId);
  const saveMutation = useSaveCredentials(projectId);

  const handlePreview = async () => {
    setError("");
    try {
      setPreview(await previewMutation.mutateAsync(text));
      setStep("review");
    } catch (err) {
      setError(errorMessage(err, "No se pudo revisar el texto."));
    }
  };

  const handleSave = async () => {
    setError("");
    try {
      setResult(await saveMutation.mutateAsync({ text, allowChanges }));
      setText(""); // la contraseña ya no se conserva en pantalla
      setStep("done");
    } catch (err) {
      setError(errorMessage(err, "No se pudo guardar."));
    }
  };

  const saveable = (preview?.items || []).filter((i) => i.status === "new" || i.status === "update").length;
  const unassigned = preview?.ignored || 0; // trozos del texto con contraseña pero sin IP (p. ej. al pegar una respuesta completa)

  return (
    <div className="space-y-3">
      {step === "paste" && (
        <>
          <Textarea
            data-testid="credentials-text"
            autoFocus
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={9}
            spellCheck={false}
            placeholder={"Pega aquí lo que tengas: IP, usuario y contraseña. Por ejemplo:\n\nWEB: IP 10.0.0.1, usuario root, contraseña …\nMikroTik 172.18.0.1 usuario panel-ro contraseña … vía WEB"}
            className="font-mono text-xs"
          />
          <label className="flex items-start gap-2 text-xs text-muted-foreground cursor-pointer">
            <input
              data-testid="credentials-allow-changes"
              type="checkbox"
              className="mt-0.5"
              checked={allowChanges}
              onChange={(e) => setAllowChanges(e.target.checked)}
            />
            <span>
              Permitir cambios en servidores Linux (la IA propone, tú confirmas con un botón; el panel respalda y puede revertir). Sin
              marcar, queda en solo lectura. Un MikroTik siempre es de solo lectura.
            </span>
          </label>
          <div className="flex justify-end">
            <Button data-testid="credentials-review" onClick={handlePreview} disabled={!text.trim() || previewMutation.isPending}>
              {previewMutation.isPending ? "Revisando..." : "Revisar"}
            </Button>
          </div>
        </>
      )}

      {step === "review" && preview && (
        <>
          {preview.items.length === 0 ? (
            <p data-testid="credentials-none" className="text-sm text-muted-foreground">
              No encontré servidores en ese texto. Incluye IP o dominio, usuario y contraseña.
            </p>
          ) : (
            <div className="space-y-2" data-testid="credentials-items">
              {preview.items.map((item, idx) => {
                const st = STATUS_LABEL[item.status] || STATUS_LABEL.problem;
                return (
                  <div key={idx} className="rounded-lg border border-border p-3 text-sm space-y-1" data-testid={`credentials-item-${idx}`}>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <span className="font-medium">{item.name || item.host || "Servidor"}</span>
                      <span className="text-xs">{st.icon} {st.text}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">{typeLabel(item.type)}{item.via ? ` · vía ${item.via}` : ""}</p>
                    {item.host && (
                      <p className="text-xs font-mono text-muted-foreground">
                        {item.username || "—"}@{item.host}:{item.port}
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground">{item.auth_method === "key" ? "Llave privada" : "Contraseña"}: ••• se guardará cifrada</p>
                    {item.weak && <p className="text-xs text-amber-500">⚠️ La contraseña es débil; conviene cambiarla.</p>}
                    {item.note && <p className="text-xs text-muted-foreground">{item.note}</p>}
                  </div>
                );
              })}
            </div>
          )}
          {unassigned > 0 && (
            <p data-testid="credentials-ignored" className="text-xs text-amber-500">Ignoré {unassigned} trozo(s) del texto que traían una contraseña pero ninguna IP o dominio.</p>
          )}
          <div className="flex justify-between gap-2">
            <Button variant="outline" onClick={() => setStep("paste")}>Atrás</Button>
            <Button data-testid="credentials-save" onClick={handleSave} disabled={saveable === 0 || saveMutation.isPending}>
              {saveMutation.isPending ? "Guardando y probando..." : `Guardar y conectar (${saveable})`}
            </Button>
          </div>
        </>
      )}

      {step === "done" && result && (
        <>
          <p data-testid="credentials-summary" className="text-sm font-medium">{result.summary}</p>
          <div className="space-y-1.5">
            {result.rows.map((row, idx) => (
              <div key={idx} className="text-sm rounded-lg border border-border p-2.5">
                <span className="font-medium">{ROW_ICON[row.status] || "🟡"} {row.name}</span>
                <p className="text-xs text-muted-foreground mt-0.5">{row.result}</p>
              </div>
            ))}
          </div>
          {result.texts.map((t, i) => (
            <p key={i} className="text-xs text-muted-foreground">{t}</p>
          ))}
          <div className="flex justify-end">
            <Button data-testid="credentials-close" onClick={onClose}>Cerrar</Button>
          </div>
        </>
      )}

      {error && <p data-testid="credentials-error" className="text-xs text-red-500">{error}</p>}
    </div>
  );
}

export function CredentialsBoxDialog({ open, onOpenChange, project }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="credentials-dialog" className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Lock className="h-4 w-4" /> Compartir credenciales — {project?.name}</DialogTitle>
          <DialogDescription>
            Pega las credenciales una sola vez. El panel las ordena, las guarda cifradas y conecta el servidor; la IA las usa pero
            nunca las ve. Puedes verlas después en «Servidores → Ver credenciales».
          </DialogDescription>
        </DialogHeader>
        {/* se monta solo abierto: al cerrar se pierde todo el texto pegado */}
        {open && <PasteBox projectId={project?.id} onClose={() => onOpenChange(false)} />}
      </DialogContent>
    </Dialog>
  );
}
